import { Select } from "../ui/select.tsx";
import { CheckCircleIcon, CloudIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { isAccountOnly } from "@zooi/domain";
import { type FormEvent, useState } from "react";
import { api, q } from "../api.ts";
import { browserTimeZone, timeZones } from "../format.ts";
import { DETAIL_LABELS, NEW_PRODUCT_LABELS as N, PRODUCT_SOURCE_LABELS as L, WORKSPACE_LABELS as W } from "../labels.ts";
import { GitHubTokenField, type ProductSourceForm, ProductSourceFields, RepositoryConsent, sourceRequest } from "../product-sources.tsx";
import { rememberProduct } from "../current-product.ts";
import { Button, Callout, CopyButton, ErrorText, Field, Page, Section, StageSteps, ViewSwitch } from "../ui.tsx";
import { AgentLogo } from "../ui/props.tsx";
import { CloudAgentSteps, cloudPrompt } from "../cloud-agent.tsx";
import { Skeleton } from "../ui/skeleton.tsx";
import { Chip } from "../ui/status.tsx";

// Adding a product (docs/design-system.md §0.4h, plan M7 step 7j): one column
// in steps. The product first; then the person connects their agent, which
// reads the repository on their computer and writes the plans. Whether the
// agent is connected is the server's word (the week's setup), never guessed.

const D = DETAIL_LABELS.newProduct;
/** An account a person grows has a folder instead of a repository, and the person describes it (2026-11-03). */
const accountOnly = (product: C.Product) => isAccountOnly({ url: product.url, repository: product.source.repository });

/** A cloud agent (OpenAI dots, Grok Bot) installs no plugin: it adds the MCP server and follows plugin/CLOUD.md. */
type Client = "claude" | "codex" | "cloud";

/** Commands, as plugin/INSTALL.md gives them; they are code, so not translated. */
const COMMANDS: Record<Exclude<Client, "cloud">, { install: string; authorize: string | null; onboard: string }> = {
  claude: { install: "claude plugin marketplace add ZooiAI/zooi-plugin\nclaude plugin install zooi@zooi", authorize: null, onboard: "/zooi:onboard" },
  codex: { install: "codex plugin marketplace add ZooiAI/zooi-plugin\ncodex plugin add zooi@zooi", authorize: "codex mcp login zooi", onboard: "$zooi:onboard" },
};

/** `/new?product=` continues with a product just added, so a reload keeps the step. */
export const newProductSearch = (search: Record<string, unknown>): { product?: string } =>
  typeof search.product === "string" && /^prd_[a-z0-9]{1,40}$/.test(search.product) ? { product: search.product } : {};

export function NewProduct() {
  const me = useQuery(q.me);
  const { product: productId } = useSearch({ strict: false }) as { product?: string };
  const product = useQuery({ ...q.product(productId ?? ""), enabled: Boolean(productId) });
  // Until the agent connects, ask again every few seconds; the setup is the server's to say.
  const week = useQuery({ ...q.week(productId ?? ""), enabled: Boolean(productId), refetchInterval: (query) => (query.state.data?.setup.agent_connected ? false : 5000) });
  const setup = week.data?.setup;
  const steps = [
    { label: N.steps.product, status: productId ? "complete" as const : "current" as const },
    { label: N.steps.agent, status: setup?.agent_connected ? "complete" as const : productId ? "current" as const : "upcoming" as const },
    { label: product.data && accountOnly(product.data) ? N.account.firstRead : N.steps.firstRead, status: setup?.facts_confirmed ? "complete" as const : setup?.agent_connected ? "current" as const : "upcoming" as const },
    { label: N.steps.firstPlan, status: setup?.first_plan_approved ? "complete" as const : setup?.facts_confirmed ? "current" as const : "upcoming" as const },
  ];
  if (!productId && me.data?.product_ids !== null) return <Page crumbs={[{ label: L.addTitle }]}><Callout>{W.selectedNote}</Callout></Page>;
  return <Page crumbs={[{ label: L.addTitle }]} title={L.addTitle} meta={N.lead}>
    <div className="z-onboarding z-flow">
      <StageSteps steps={steps} />
      {!productId ? <ProductStep />
        : product.isLoading ? <Skeleton shape="list" rows={1} />
        : product.data ? <>
          <AddedProduct product={product.data} />
          <AgentStep product={product.data} connected={Boolean(setup?.agent_connected)} />
          <NextSteps productId={product.data.id} connected={Boolean(setup?.agent_connected)} account={accountOnly(product.data)} />
        </>
        : <ErrorText error={product.error} />}
      <ErrorText error={week.error} />
      <Callout>{product.data && accountOnly(product.data) ? N.account.privacy : N.privacy}</Callout>
    </div>
  </Page>;
}

/** Step 1: what the product is and where its sources are. */
function ProductStep() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [timezone, setTimezone] = useState(browserTimeZone());
  const [source, setSource] = useState<ProductSourceForm>({ repository: "", repositoryRef: "", sourceOfTruth: "website", websiteStatus: "current" });
  const [token, setToken] = useState("");
  const [consent, setConsent] = useState(false);
  const hasRepository = source.repository.trim().length > 0;
  const create = useMutation({
    mutationFn: () => api.post<C.Product>("/api/products", {
      url,
      timezone,
      ...(name.trim() ? { name: name.trim() } : {}),
      ...sourceRequest(source, consent),
      ...(hasRepository && token.trim() ? { github_token: token.trim() } : {}),
    }),
    onSuccess: async (p) => {
      setToken("");
      await client.invalidateQueries({ queryKey: ["products"] });
      rememberProduct(p.id);
      // The next step is connecting the agent, on this page.
      void navigate({ to: "/new", search: { product: p.id }, replace: true });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (hasRepository && !consent) return;
    create.mutate();
  };
  return <Section title={N.steps.product}>
    <form onSubmit={submit} className="z-form z-flow">
      <Field label={N.name} help={N.nameHelp}>
        <input className="z-input" maxLength={80} value={name} disabled={create.isPending} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={L.website} help={L.websiteHelp}>
        <input className="z-input" required placeholder={L.websitePlaceholder} maxLength={500} value={url} disabled={create.isPending} onChange={(e) => setUrl(e.target.value)} />
      </Field>
      <details className="z-disclosure">
        <summary>{D.repository}</summary>
        <p className="z-small z-muted">{D.repositoryNote}</p>
        <ProductSourceFields value={source} onChange={(value) => {
          setSource(value);
          setConsent(false);
          if (value.repository !== source.repository) setToken("");
        }} disabled={create.isPending} />
      </details>
      {hasRepository && <div className="z-flow">
        <GitHubTokenField value={token} onChange={setToken} disabled={create.isPending} />
        <RepositoryConsent checked={consent} onChange={setConsent} disabled={create.isPending} />
        <p className="z-small z-muted">{L.readOnly}</p>
      </div>}
      <Field label={L.timezone} help={L.timezoneHelp}>
        <Select variant="form" value={timezone} disabled={create.isPending} onValueChange={(nextValue) => setTimezone(nextValue)} options={timeZones().map((z) => ({ value: z, label: z }))} />
      </Field>
      <div><Button tone="primary" type="submit" busy={create.isPending} disabled={hasRepository && !consent}>{N.add}</Button></div>
      {create.isPending && <p role="status" className="z-small z-muted">{D.pending}</p>}
      <ErrorText error={create.error} />
    </form>
  </Section>;
}

/** Step 1, done: the product in one line, which can still be changed in its settings. */
function AddedProduct({ product }: { product: C.ProductDetail }) {
  return <div className="z-boxed z-added">
    <CheckCircleIcon size={16} weight="fill" className="z-added-icon" aria-hidden />
    <span className="z-added-name">{product.name}</span>
    <span className="z-small z-muted">{product.url.replace(/^https?:\/\//, "").replace(/\/$/, "")} · {product.settings.timezone}</span>
    <Link className="z-iconbtn" to="/settings/products/$productId" params={{ productId: product.id }} aria-label={N.edit} title={N.edit}><PencilSimpleIcon size={14} /></Link>
  </div>;
}

/** Step 2: install the plugin, sign in and allow, start the first read. */
function AgentStep({ product, connected }: { product: C.ProductDetail; connected: boolean }) {
  const [client, setClient] = useState<Client>("claude");
  return <Section title={N.steps.agent} actions={<ViewSwitch label={N.client} value={client} onValueChange={setClient}
    options={[
      { value: "claude", label: "Claude Code", icon: <AgentLogo engine="claude_code" size={14} /> },
      { value: "codex", label: "Codex", icon: <AgentLogo engine="codex" size={14} /> },
      { value: "cloud", label: N.cloud, icon: <CloudIcon size={14} aria-hidden /> },
    ]} />}>
    {client === "cloud" ? <CloudAgentStep product={product} connected={connected} /> : <PluginSteps product={product} client={client} connected={connected} />}
  </Section>;
}

function PluginSteps({ product, client, connected }: { product: C.ProductDetail; client: Exclude<Client, "cloud">; connected: boolean }) {
  const commands = COMMANDS[client];
  const copy = accountOnly(product) ? N.account : N;
  const forAgent = copy.agentPrompt(product.name, window.location.origin, commands.install.replace("\n", " && "), commands.onboard);
  return <>
    <ol className="z-setup-steps">
      <li>
        <h3>{N.install}</h3>
        <p className="z-small z-muted">{copy.installNote}</p>
        <div className="z-cli"><pre className="z-mono">{commands.install}</pre><CopyButton text={commands.install} /></div>
      </li>
      <li>
        <h3>{N.authorize}</h3>
        <p className="z-small z-muted">{client === "claude" ? copy.authorizeClaude : N.authorizeCodex}</p>
        {commands.authorize && <div className="z-cli"><pre className="z-mono">{commands.authorize}</pre><CopyButton text={commands.authorize} /></div>}
      </li>
      <li>
        <h3>{copy.onboard}</h3>
        <p className="z-small z-muted">{copy.onboardNote}</p>
        <div className="z-cli"><pre className="z-mono">{commands.onboard}</pre><CopyButton text={commands.onboard} /></div>
      </li>
    </ol>
    <ConnectionStatus connected={connected} copy={forAgent} />
  </>;
}

function CloudAgentStep({ product, connected }: { product: C.ProductDetail; connected: boolean }) {
  return <CloudAgentSteps footer={<ConnectionStatus connected={connected} copy={cloudPrompt(product)} />} />;
}

function ConnectionStatus({ connected, copy }: { connected: boolean; copy: string }) {
  return <div className="z-inline" role="status">
    {connected ? <Chip variant="soft" tone="green" dot>{N.connected}</Chip> : <span className="z-small z-muted">{N.waiting}</span>}
    <CopyButton text={copy} label={N.copyForAgent} />
  </div>;
}

/** Step 3 and 4: what happens after the agent connects, and where the product lives from now on. */
function NextSteps({ productId, connected, account }: { productId: string; connected: boolean; account: boolean }) {
  return <Section title={N.next}>
    <ol className="z-flowline">{(account ? N.account : N).flow.map((step) => <li key={step}>{step}</li>)}</ol>
    <p><Link to="/p/$productId/week" params={{ productId }} className={`z-btn${connected ? " primary" : ""}`}>{N.openWeek}</Link></p>
  </Section>;
}
